import { PricingItemTypeEnum } from '../../pricing-items/enums/pricing-item-type.enum';
import type { QuotationDto } from '../dtos/quotation.dto';
import type { PricingItemTotalsInput } from '../interfaces/pricing-item-totals-input.interface';
import type { StandardDiscountDefaults } from '../interfaces/standard-discount-defaults.interface';

import { calculateQuotation } from './calculate-quotation.function';
import { resolveIsStandardDiscountApplied } from './resolve-is-standard-discount-applied.function';

/**
 * Computes discounted record totals from pricing items. The discount applies
 * only to STANDARD items explicitly flagged isStandardDiscountApplied;
 * unflagged STANDARD items stay in the standard bucket undiscounted; all
 * other types are extras.
 */
export function calculateDiscountedTotalsFromPricingItems(
  items: PricingItemTotalsInput[] | null | undefined,
  standardDiscountPercentage: number,
  standardDiscountDefaults?: StandardDiscountDefaults,
): QuotationDto {
  const zero = () => ({ net: 0, tax: 0, gross: 0 });
  const discountBase = zero();
  const standardNonDiscounted = zero();
  const extras = zero();
  const isStandardDiscountEnabled =
    standardDiscountDefaults?.isStandardDiscountEnabled ?? false;
  const effectivePercentage = isStandardDiscountEnabled
    ? Number(standardDiscountPercentage ?? 0)
    : 0;

  for (const item of items ?? []) {
    const itemType = item.type;
    const isDiscountApplied =
      itemType != null
        ? resolveIsStandardDiscountApplied(
            itemType,
            item.isStandardDiscountApplied,
            isStandardDiscountEnabled,
          )
        : false;
    const bucket =
      itemType === PricingItemTypeEnum.STANDARD
        ? isDiscountApplied
          ? discountBase
          : standardNonDiscounted
        : extras;

    bucket.net += Number(item.unitsTotalNet ?? 0);
    bucket.tax += Number(item.unitsTotalTax ?? 0);
    bucket.gross += Number(item.unitsTotalGross ?? 0);
  }

  return calculateQuotation({
    discountableBaseTotalNet: discountBase.net,
    discountableBaseTotalTax: discountBase.tax,
    discountableBaseTotalGross: discountBase.gross,
    standardNonDiscountedBaseTotalNet: standardNonDiscounted.net,
    standardNonDiscountedBaseTotalTax: standardNonDiscounted.tax,
    standardNonDiscountedBaseTotalGross: standardNonDiscounted.gross,
    nonDiscountableBaseTotalNet: extras.net,
    nonDiscountableBaseTotalTax: extras.tax,
    nonDiscountableBaseTotalGross: extras.gross,
    standardDiscountPercentage: effectivePercentage,
    isStandardDiscountEnabled,
  });
}
